import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PublicHeader from './components/Header/PublicHeader';
import PublicFooter from './components/Footer/PublicFooter';

interface PublicLayoutProps {
  header?: React.ReactNode | 'default' | 'none';
  footer?: React.ReactNode | 'default' | 'none';
  bodyClassName?: string;
  showHeader?: boolean;
}

/**
 * Layout component for public pages (Home, etc.)
 * Structure: Header + Body + Footer
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({
  header = 'default',
  footer = 'none',
  bodyClassName = '',
  showHeader = true,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  // Hide header on support page; show for other public pages
  const shouldShowHeader = showHeader && pathname !== '/support';
  // Home: fixed 80px header, body takes rest, footer 80px is inside page (OrderSummaryFooter)
  const isHome = pathname === '/';

  return (
    <div
      className={
        isHome
          ? 'safe-area-top safe-area-bottom h-screen flex flex-col overflow-hidden'
          : 'safe-area-top safe-area-bottom min-h-screen flex flex-col overflow-x-hidden'
      }
    >
      {/* Header - 80px on home */}
      {shouldShowHeader && (header === 'none' ? null : header === 'default' ? (
        isHome ? (
          <div className="relative z-30 h-[80px] flex-shrink-0 overflow-visible">
            <PublicHeader compact />
          </div>
        ) : (
          <PublicHeader />
        )
      ) : (
        header
      ))}

      {/* Body - flex-1 min-h-0 on home so it gets remaining height without scroll */}
      <main
        className={
          isHome
            ? `flex-1 min-h-0 min-w-0 overflow-hidden ${bodyClassName}`
            : `flex-1 min-w-0 ${bodyClassName}`
        }
      >
        <Outlet />
      </main>

      {/* Footer - not shown on home (footer is OrderSummaryFooter inside page) */}
      {!isHome &&
        (footer === 'none' ? null : footer === 'default' ? (
          <PublicFooter />
        ) : (
          footer
        ))}
    </div>
  );
};

export default PublicLayout;
